import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { LocalStorageStore } from '@qtek/core/local-storage-management';
import { BackendResponse, HTTP_BASE_URL, Language } from '@qtek/shared/models';
import { isNonNullable, isNullable } from '@qtek/shared/utils';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { MetaCoreStore } from '@qtek/libs/meta-core';
import {
  DebugTranslationsDI,
  LanguagesArray,
  passWhenTrue,
} from '../models/translation-core.model';
import { TranslationCoreStore } from '../store/translation-core.store';

// export function isNonNullable<T>(value: T | undefined | null): value is T {
//   return value !== null && value !== undefined;
// }
//
// export function isNullable<T>(value: T | undefined | null): value is undefined | null {
//   return value === null || value === undefined;
// }

@Injectable({
  providedIn: 'root',
})
export class TranslationCoreService {
  private http = inject(HttpClient);
  private translationStore = inject(TranslationCoreStore);
  private localStorageStore = inject(LocalStorageStore);
  private metaCoreStore = inject(MetaCoreStore);
  private httpBaseUrl = inject(HTTP_BASE_URL);
  private debugTranslation = inject(DebugTranslationsDI, { optional: true })!;

  private readonly isTranslationsInitialized$ = new BehaviorSubject(false);

  private readonly storeTranslations$ = toObservable(
    this.translationStore.translations
  );
  private readonly storeLang$ = toObservable(this.translationStore.lang);
  private readonly isLocalStorageLoaded$ = toObservable(
    this.localStorageStore.isInitialized
  );
  private readonly localStorageTranslations$ = toObservable(
    this.localStorageStore.i18n
  );
  private readonly wsVersion$ = toObservable(
    this.metaCoreStore.websocketVersion
  );

  private readonly cachedTranslationsSubject = new BehaviorSubject<
    Record<Language, Record<string, string>> | undefined
  >(undefined);
  private readonly cachedLangSubject = new BehaviorSubject<
    Language | undefined
  >(undefined);

  public get selectedLang(): Language {
    return this.getCachedLanguage() ?? 'en_US';
  }

  public selectedLang$ = this.getCachedLanguageSubject();

  // cache translations
  private translationsState$ = this.storeTranslations$.pipe(
    distinctUntilChanged(),
    passWhenTrue(this.isTranslationsInitialized$),
    tap(translations => {
      this.cachedTranslationsSubject.next(translations);
    }),
    shareReplay(1),
    takeUntilDestroyed()
  );

  // cache language
  private languageState$ = this.storeLang$.pipe(
    passWhenTrue(this.isTranslationsInitialized$),
    // filter initial fast changes
    debounceTime(50),
    distinctUntilChanged(),
    filter(lang => isNonNullable(lang)),
    tap(lang => {
      this.cachedLangSubject.next(lang);
    }),
    shareReplay(1),
    takeUntilDestroyed()
  );

  private currentTranslations$ = combineLatest([
    this.translationsState$,
    this.languageState$,
  ]).pipe(
    passWhenTrue(this.isTranslationsInitialized$),
    map(([translations, lang]) => translations?.[lang]),
    shareReplay(1),
    takeUntilDestroyed()
  );

  /** Emits when translations or language change. Use in pipes to trigger re-render (e.g. columnHeader). */
  readonly translationsOrLang$ = this.currentTranslations$;

  constructor() {
    this.initializeTranslations().subscribe();

    this.currentTranslations$
      .pipe(
        filter(
          translations =>
            isNullable(translations) ||
            (typeof translations === 'object' &&
              Object.keys(translations).length === 0)
        ),
        map(() => this.translationStore.lang()),
        switchMap(lang => {
          console.log('fetch lang: ', lang);

          return this.fetchTranslations(lang);
        }),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  private initializeTranslations(): Observable<unknown> {
    const wsVersion$ = this.wsVersion$.pipe(filter(v => isNonNullable(v)));

    return combineLatest([this.localStorageTranslations$, wsVersion$]).pipe(
      passWhenTrue(this.isLocalStorageLoaded$),
      take(1),
      tap(([translationsStr, wsVersion]) => {
        // Handle no storage translations
        if (isNullable(translationsStr)) {
          // Set default language
          this.applyLanguage(this.getUserBrowserLanguageCode());
          // Set default translations
          this.translationStore.setTranslations({
            en_US: {},
            pl_PL: {},
            es_US: {},
          });
        } else {
          const { translations, lang, version } = JSON.parse(translationsStr);

          // Handle version mismatch
          if (wsVersion !== version) {
            // Delete translations from local storage
            this.localStorageStore.deleteByKey('i18n');
            // Set default translations
            this.translationStore.setTranslations({
              en_US: {},
              pl_PL: {},
              es_US: {},
            });
          } else {
            // Set translations from local storage
            this.translationStore.setTranslations(translations);
          }

          // Set language from local storage
          this.applyLanguage(lang);
        }

        this.translationStore.setVersion(wsVersion);

        this.saveTranslationToCache();

        this.isTranslationsInitialized$.next(true);
      })
    );
  }

  private fetchTranslations(
    lang: Language
  ): Observable<BackendResponse<Record<string, string>>> {
    return this.http
      .get<
        BackendResponse<Record<string, string>>
      >(`${this.httpBaseUrl}/api/v2/i18n?lng=${lang}`)
      .pipe(
        tap(({ res }) => {
          this.translationStore.mergeTranslations(res, lang);
          this.saveTranslationToCache();
        })
      );
  }

  public setLanguage(lang: Language) {
    this.isTranslationsInitialized$
      .pipe(
        filter(Boolean),
        take(1),
        tap(() => this.applyLanguage(lang))
      )
      .subscribe();
  }

  public getTranslations(keys: string[]): Observable<any> {
    return this.currentTranslations$.pipe(
      filter(
        translations =>
          isNonNullable(translations) ||
          (typeof translations === 'object' &&
            Object.keys(translations).length > 0)
      ),
      map(translations => {
        return keys.reduce((acc, key) => {
          acc[key] = translations[key] ?? (this.debugTranslation ? key : '');
          return acc;
        }, {} as any);
      })
    );
  }

  public getTranslation(
    key: string,
    options?: { params?: Array<any> }
  ): Observable<string> {
    return this.currentTranslations$.pipe(
      filter(translations => {
        if (isNullable(translations) || typeof translations !== 'object')
          return false;
        // stops on the first key, no allocation, O(1) when the object has keys
        for (const _ in translations as object) return true;
        return false;
      }),
      map(translations => {
        const translation = translations?.[key];

        if (!translation) {
          return this.debugTranslation ? key : '';
        }
        return options?.params
          ? this.interpolate(translation, options.params)
          : translation;
      })
    );
  }

  /** Fallback labels when the backend does not provide a translation. */
  private static readonly FALLBACK_LABELS: Record<string, string> = {
    FTR_VIZ_TREND_VS_PRIOR: 'vs prior',
    GNR_NOTE_AUTHOR: 'Note Author',
    GNR_BENCHMARKING_FIELDS: 'Benchmarking fields',
    GNR_BENCHMARKING_FIELDS_DESC:
      'Fields marked with this icon are used in deal benchmarking and comparisons',
    GNR_CMP_ALLOW_ALPHA: 'Company allows Alpha features',
    GNR_BENCHMARK_STAGE_REQUIRED_AGENCY: 'Stage must be Agency',
    GNR_BENCHMARK_MISSING_FIELDS_HINT:
      'Fill in the missing benchmark fields below to continue.',
    GNR_BENCHMARK_READY_PROMPT:
      'All benchmark fields are complete. Publish this deal to the benchmark?',
    GNR_BENCHMARK_READY_PROMPT_CTA: 'Publish',
    GNR_MOVE_TO_AGENCY_TITLE: 'Move this deal to Agency?',
    GNR_MOVE_TO_AGENCY_MESSAGE:
      'Moving <strong>{1}</strong> to the Agency stage will remove it from your <strong>Sales Pipeline</strong> and move it to the <strong>Agency tab</strong>.',
    GNR_MOVE_TO_AGENCY_CTA: 'Move to Agency',
    GNR_ALLO_CHART_FAILED: "Couldn't render that chart.",
    GNR_PDF_PREVIEW_FAILED: "Couldn't load the preview for this document.",
    GNR_ALLO_CHART_EXPAND: 'Expand chart',
    GNR_ALLO_THINKING: 'Allo is thinking',
    GNR_ALLO_TOOL_USED_ONE: 'Used 1 tool',
    GNR_ALLO_TOOLS_USED: 'Used {1} tools',

    // SD-2802: the strings that explain a failure or offer a way out of one. They
    // are needed exactly when something has already gone wrong — which is also when
    // a partly-deployed or misconfigured environment is most likely — and without a
    // fallback `instant()` returns '' in production, leaving a control with no text
    // at all. Copy is taken verbatim from lib-v2's translations.tsv so a fallback
    // never contradicts the served row.
    GNR_ALLO_STREAM_FAILED:
      'Something went wrong generating the response. Please try again.',
    GNR_ALLO_STREAM_INTERRUPTED: 'Response interrupted. Please try again.',
    GNR_ALLO_RETRY: 'Retry',
    GNR_ALLO_PROPOSED_ACTION: 'Proposed action',
    GNR_ALLO_APPROVED: 'Approved',
    GNR_ALLO_REJECTED: 'Rejected',
    GNR_ALLO_SUBMITTING: 'Submitting...',
    GNR_ALLO_APPROVAL_FAILED:
      'Failed to submit your decision. Please try again.',
    GNR_ALLO_RESOLVE_PENDING: 'Approve or reject the action above to continue.',
    GNR_ALLO_CHAT_UNAVAILABLE: 'Chat is currently unavailable.',
    GNR_ALLO_CHAT_OUTSIDE_HOURS:
      'Chat is unavailable outside of working hours.',
    GNR_ALLO_YOUR_QUESTION: 'Your question',
    GNR_ALLO_MESSAGE_SUPPORT: 'Message support',
    GNR_ALLO_MEMORY_LOAD_FAILED: 'Could not load your chat memory settings.',
    GNR_ALLO_MEMORY_SAVE_FAILED:
      'Could not save your chat memory setting. Please try again.',
    GNR_ALLO_MEMORY_CLEAR_FAILED:
      'Could not clear your saved preferences. Please try again.',
    GNR_TASK_CREATION_FAILED: 'Failed to create task. Please try again.',
    GNR_TASK_CREATION_TIMEOUT: 'Task creation timed out. Please try again.',
    // No row exists for this key in lib-v2's translations.tsv (nor v1's), so it
    // renders empty today on every environment even when translations load
    // normally. This fallback is the stopgap; the BE row is the fix.
    GNR_ALLO_STILL_WORKING: 'Still working on it...',
    // SD-2821: the send-email card's copy controls. Without a fallback, an unloaded
    // translation would leave a button with no label.
    GNR_ALLO_EMAIL_COPY_TO: 'Copy recipients',
    GNR_ALLO_EMAIL_COPY_SUBJECT: 'Copy subject',
    GNR_ALLO_EMAIL_COPY_MESSAGE: 'Copy message',
    GNR_ALLO_EMAIL_COPIED: 'Copied',
    GNR_ALLO_EMAIL_PASTE_HINT:
      'Paste the contents of the email directly into your email client.',
    // SD-2924 - Service worker, temporary translations
    ERR_NET_REQUEST_BLOCKED:
      'This request was blocked by your network or security software. Contact your IT administrator to allow traffic to synply.io',
  };

  public instant(key: string, params?: Array<any>): string {
    const translations = this.getCachedTranslations();
    const lang = this.getCachedLanguage() ?? 'en_US';
    const translation = translations?.[lang]?.[key];

    if (!translation) {
      const fallback = TranslationCoreService.FALLBACK_LABELS[key];
      if (fallback)
        return params ? this.interpolate(fallback, params) : fallback;
      return this.debugTranslation ? key : '';
    }

    return params ? this.interpolate(translation, params) : translation;
  }

  /**
   * Bulk synchronous translation for many keys. Uses cached translations.
   * Returns a map key -> translated string (or fallback / key / '' per key).
   */
  public instantMany(keys: string[]): Record<string, string> {
    const translations = this.getCachedTranslations();
    const lang = this.getCachedLanguage() ?? 'en_US';
    const map = translations?.[lang];
    const result: Record<string, string> = {};
    for (const key of keys) {
      const t = map?.[key];
      if (t != null) {
        result[key] = t;
      } else {
        const fallback = TranslationCoreService.FALLBACK_LABELS[key];
        result[key] = fallback ?? (this.debugTranslation ? key : '');
      }
    }
    return result;
  }

  saveTranslationToCache(): void {
    this.isTranslationsInitialized$
      .pipe(filter(Boolean), take(1))
      .subscribe(() => {
        const translations = this.translationStore.translations();
        const lang = this.translationStore.lang();
        const version = this.translationStore.version();
        if (
          isNullable(translations) ||
          isNullable(lang) ||
          isNullable(version)
        ) {
          return;
        }
        this.localStorageStore.saveByKey(
          'i18n',
          JSON.stringify({ translations, lang, version })
        );
      });
  }

  // TODO: Fix duplication
  public setupNoNetworkTranslations(): void {
    this.localStorageTranslations$
      .pipe(
        passWhenTrue(this.isLocalStorageLoaded$),
        take(1),
        tap(translationsStr => {
          // Handle no storage translations
          if (isNullable(translationsStr)) {
            // Set default language
            this.applyLanguage(this.getUserBrowserLanguageCode());
            // Set default translations
            this.translationStore.setTranslations({
              en_US: {},
              pl_PL: {},
              es_US: {},
            });
          } else {
            const { translations, lang, version } = JSON.parse(translationsStr);

            // Set translations from local storage
            this.translationStore.setTranslations(translations);

            // Set language from local storage
            this.applyLanguage(lang);

            this.saveTranslationToCache();

            this.isTranslationsInitialized$.next(true);
          }
        })
      )
      .subscribe();
  }

  getCachedTranslations():
    | Record<'en_US' | 'es_US' | 'pl_PL', Record<string, string>>
    | undefined {
    return this.cachedTranslationsSubject.getValue();
  }

  private getCachedLanguage(): 'en_US' | 'es_US' | 'pl_PL' | undefined {
    return this.cachedLangSubject.getValue();
  }

  private getCachedLanguageSubject(): BehaviorSubject<
    'en_US' | 'es_US' | 'pl_PL' | undefined
  > {
    return this.cachedLangSubject;
  }

  public getLanguages$(): Observable<Language[]> {
    return of(LanguagesArray);
  }

  private interpolate(expression: string, params?: Array<any>): string {
    if (typeof expression !== 'string' || !params || !Array.isArray(params)) {
      return expression;
    }

    return params.reduce(
      (message, value, index) => message.replace(`{${index + 1}}`, value),
      expression
    );
  }

  private applyLanguage(lang: Language): void {
    this.translationStore.setLanguage(lang);
    this.saveTranslationToCache();
  }

  private getUserBrowserLanguageCode(): Language {
    const userLang = navigator.language;
    const code = userLang[0] + userLang[1];
    let languageCode: Language = 'en_US';

    switch (code) {
      case 'pl': {
        languageCode = 'pl_PL';
        break;
      }
      case 'es': {
        languageCode = 'es_US';
        break;
      }
      default:
        break;
    }
    return languageCode;
  }
}
